import { Capacitor } from "@capacitor/core";

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;
const DEBUG =
    import.meta.env.DEV ||
    String(import.meta.env.VITE_META_PIXEL_DEBUG || "").toLowerCase() === "true";

let initialized = false;
let lastPageViewKey = "";

function isBrowser() {
    return typeof window !== "undefined" && typeof document !== "undefined";
}

function isNativeApp() {
    return Capacitor.isNativePlatform();
}

function debugLog(...args) {
    if (DEBUG) console.log("[Meta Pixel]", ...args);
}

function debugWarn(...args) {
    if (DEBUG) console.warn("[Meta Pixel]", ...args);
}

function cleanMetaParams(params = {}) {
    return Object.fromEntries(
        Object.entries(params || {}).filter(([, value]) => {
            if (value === undefined || value === null) return false;
            if (typeof value === "string" && value.trim() === "") return false;
            return true;
        })
    );
}

export function initMetaPixel() {
    if (!PIXEL_ID) {
        debugWarn("Missing VITE_META_PIXEL_ID");
        return false;
    }

    if (isNativeApp()) {
        debugLog("Skipped init inside native app");
        return false;
    }

    if (!isBrowser()) return false;

    if (initialized && window.fbq) return true;

    initialized = true;

    !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
            n.callMethod
                ? n.callMethod.apply(n, arguments)
                : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
    })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js"
    );

    window.fbq("init", PIXEL_ID);
    debugLog("Initialized", PIXEL_ID);
    return true;
}

function fireFbq(command, eventName, params = {}, options = {}) {
    if (!initMetaPixel()) return false;
    if (!window.fbq) {
        debugWarn("fbq unavailable", eventName, params);
        return false;
    }

    const cleanParams = cleanMetaParams(params);
    const cleanOptions = cleanMetaParams(options);

    try {
        if (cleanOptions.eventID) {
            window.fbq(command, eventName, cleanParams, {
                eventID: cleanOptions.eventID,
            });
        } else {
            window.fbq(command, eventName, cleanParams);
        }

        debugLog(command, eventName, cleanParams, cleanOptions);
        return true;
    } catch (error) {
        debugWarn("Event failed", command, eventName, error);
        return false;
    }
}

export function trackMetaEvent(eventName, params = {}, options = {}) {
    return fireFbq("track", eventName, params, options);
}

export function trackMetaCustomEvent(eventName, params = {}, options = {}) {
    return fireFbq("trackCustom", eventName, params, options);
}

export function trackMetaPageView(params = {}, dedupeKey = "") {
    const key =
        dedupeKey ||
        (isBrowser()
            ? `${window.location.pathname}${window.location.search}`
            : "unknown");

    if (key && key === lastPageViewKey) {
        debugLog("Skipped duplicate PageView", key);
        return false;
    }

    lastPageViewKey = key;
    return trackMetaEvent("PageView", params);
}

export function getCookieValue(name) {
    if (!isBrowser()) return "";

    const cookie = document.cookie
        .split(";")
        .map((part) => part.trim())
        .find((part) => part.startsWith(`${name}=`));

    if (!cookie) return "";

    try {
        return decodeURIComponent(cookie.slice(name.length + 1));
    } catch {
        return cookie.slice(name.length + 1);
    }
}

export function getMetaAttributionPayload() {
    if (!isBrowser()) {
        return {
            meta_fbp: "",
            meta_fbc: "",
            meta_client_user_agent: "",
        };
    }

    return {
        meta_fbp: getCookieValue("_fbp"),
        meta_fbc: getCookieValue("_fbc"),
        meta_client_user_agent: window.navigator?.userAgent || "",
    };
}

export function buildMetaEventId(prefix = "bbp_event") {
    const randomPart =
        typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

    return `${prefix}_${randomPart}`;
}
